import { useCallback, useState } from "react";
import MapPane from "../components/MapPane";
import { APP_VERSION } from "../App";
import {
  copyText,
  downloadText,
  formatCoord,
  renderExport,
  type ExportFormat,
} from "../lib/export";
import type { CaptureNotebook } from "../types";

type Props = {
  notebook: CaptureNotebook;
  onUpdate: (next: CaptureNotebook) => void;
  onReset: () => void;
};

export default function CoordsPage({ notebook, onUpdate, onReset }: Props) {
  const [format, setFormat] = useState<ExportFormat>("ts");
  const [copied, setCopied] = useState(false);

  const blocks = [...notebook.blocks].sort((a, b) => a.name.localeCompare(b.name));
  const text = renderExport(notebook, format, APP_VERSION);

  const doCopy = useCallback(async () => {
    if (await copyText(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [text]);

  const remove = useCallback(
    (name: string) => {
      onUpdate({
        ...notebook,
        blocks: notebook.blocks.filter((b) => b.name !== name),
      });
    },
    [notebook, onUpdate],
  );

  if (blocks.length === 0) {
    return (
      <div className="empty">
        <h1>No coordinates captured yet</h1>
        <p>
          Head to the <strong>Capture</strong> tab, stand at a campus block inside the green
          zone, and record the live GPS fix (or tap the map to pin it).
        </p>
      </div>
    );
  }

  return (
    <div className="coords">
      <div className="coords-layout">
        <div className="card coords-map">
          <div className="card-head">
            <h2>All captured coords</h2>
            <span className="pill pill-neutral">{blocks.length} captured</span>
          </div>
          <MapPane blocks={blocks} gps={null} pin={null} />
        </div>

        <div className="card coords-list-card">
          <div className="coords-actions">
            <button className="btn" onClick={doCopy}>
              {copied ? "Copied ✓" : "Copy"}
            </button>
            <button
              className="btn"
              onClick={() => downloadText(`attenducsi-captures.json`, renderExport(notebook, "json", APP_VERSION))}
            >
              Download JSON
            </button>
            <button className="btn btn-danger" onClick={onReset}>
              Reset all
            </button>
          </div>

          <div className="seg">
            <button className={format === "ts" ? "seg-btn seg-active" : "seg-btn"} onClick={() => setFormat("ts")}>
              builtin.ts
            </button>
            <button className={format === "cli" ? "seg-btn seg-active" : "seg-btn"} onClick={() => setFormat("cli")}>
              CLI commands
            </button>
            <button className={format === "json" ? "seg-btn seg-active" : "seg-btn"} onClick={() => setFormat("json")}>
              JSON
            </button>
          </div>

          <pre className="code">{text}</pre>

          <table className="table">
            <thead>
              <tr>
                <th>Block</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Accuracy</th>
                <th>Source</th>
                <th>Captured</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.name}>
                  <td>
                    <strong>{b.label ?? b.name}</strong>
                    <div className="muted">{b.name}</div>
                  </td>
                  <td>{formatCoord(b.latitude)}</td>
                  <td>{formatCoord(b.longitude)}</td>
                  <td>{b.accuracy !== undefined ? `${Math.round(b.accuracy)} m` : "—"}</td>
                  <td>{b.source === "gps" ? "GPS" : "map pin"}</td>
                  <td>{new Date(b.capturedAt).toLocaleString()}</td>
                  <td>
                    <button className="btn btn-ghost btn-tiny danger" onClick={() => remove(b.name)} aria-label={`Remove ${b.name}`}>
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}